import { useCallback, useMemo } from 'react'
import { View } from 'react-native'
import { SectionErrorState } from '@/src/components/ui/SectionErrorState'
import { Spinner } from '@/src/components/ui/Spinner'
import { SessionDetails, SessionSummary } from '@/src/dal/session-dal'
import { useStartVoiceAssessmentSession } from '@/src/features/voice-assessment/hooks/useStartVoiceAssessmentSession'
import { useAppSessionDefinitionIds } from '@/src/hooks/useAppSessionDefinitionIds'
import { useContentfulLocale } from '@/src/hooks/useContentfulLocale'
import {
  dashboardInfoQueryOptions,
  exerciseStatsQueryOptions,
} from '@/src/queries/activity-queries'
import { exerciseWeekQueryOptions } from '@/src/queries/exercise-queries'
import { queryKeys } from '@/src/queries/query-keys'
import {
  backendSessionQueryOptions,
  sessionDefinitionQueryOptions,
  useBackendSessionsQuery,
} from '@/src/queries/session-queries'
import { warmupWeekdayCompletionQueryOptions } from '@/src/queries/warmup-queries'
import { getCurrentYearWeek } from '@/src/utils/year-week'
import { useQueries, useQuery, useQueryClient } from '@tanstack/react-query'
import { useTranslation } from 'react-i18next'

import { EXERCISE_GOAL_CARD_STYLE } from '../../constants/statistics.styles'
import { toDateLabel } from '../../utils/map-backend-sessions-to-voice-assessment-statistics'
import { selectVoiceAssessmentSessions } from '../../utils/select-voice-assessment-sessions'
import { VoiceAssessmentExpandedDetailContentBoundary } from './VoiceAssessmentAccordionRow'
import { VoiceAssessmentLatestAnalyzingCard } from './VoiceAssessmentLatestAnalyzingCard'
import { VoiceAssessmentLatestAssessmentEmptyCard } from './VoiceAssessmentLatestAssessmentEmptyCard'
import { VoiceAssessmentRangeCard } from './VoiceAssessmentRangeCard'
import { VoiceAssessmentSectionHeader } from './VoiceAssessmentSectionHeader'
import { VoiceAssessmentSingingScoreCard } from './VoiceAssessmentSingingScoreCard'

export function LatestVoiceAssessmentSection() {
  const { t } = useTranslation(['statistics'])
  const queryClient = useQueryClient()
  const sessionDefinitionIds = useAppSessionDefinitionIds()
  const yearWeek = getCurrentYearWeek()
  const handlePendingSessionSettled = useCallback(
    (session: SessionDetails) => {
      void Promise.all([
        queryClient.invalidateQueries({
          queryKey: queryKeys.backendSessions.all(),
        }),
        queryClient.invalidateQueries({
          queryKey: queryKeys.backendSessions.byId(session.id),
        }),
        queryClient.invalidateQueries({
          queryKey: dashboardInfoQueryOptions(yearWeek).queryKey,
          exact: true,
        }),
        queryClient.invalidateQueries({
          queryKey: exerciseStatsQueryOptions.queryKey,
          exact: true,
        }),
        queryClient.invalidateQueries({
          queryKey: exerciseWeekQueryOptions(yearWeek).queryKey,
          exact: true,
        }),
        queryClient.invalidateQueries({
          queryKey: warmupWeekdayCompletionQueryOptions(yearWeek).queryKey,
          exact: true,
        }),
      ])
    },
    [queryClient, yearWeek],
  )
  const { data: sessions = [], refetch: refetchSessions } =
    useBackendSessionsQuery({
      onPendingSessionSettled: handlePendingSessionSettled,
    })

  const voiceAssessmentSessions = useMemo(
    () => selectVoiceAssessmentSessions(sessions, sessionDefinitionIds),
    [sessionDefinitionIds, sessions],
  )

  const completedSessions = useMemo(
    () =>
      voiceAssessmentSessions.filter(
        (session) => session.status === 'Completed',
      ),
    [voiceAssessmentSessions],
  )

  const latestCompletedSession = completedSessions[0]

  const completedSessionDetailsQuery = useQueries({
    queries: completedSessions.map((session) =>
      backendSessionQueryOptions(session.id),
    ),
    combine: (results) => {
      const data: SessionDetails[] = []
      let latestCompletedSessionDetail: SessionDetails | undefined
      let isLatestPending = false
      let isLatestError = false

      results.forEach((result, index) => {
        const session = completedSessions[index]
        const isLatestCompletedSession =
          session?.id === latestCompletedSession?.id

        if (result.data != null) {
          data.push(result.data)
        }

        if (isLatestCompletedSession) {
          latestCompletedSessionDetail = result.data
          isLatestPending = result.isPending
          isLatestError = result.isError
        }
      })

      return {
        data,
        latestCompletedSessionDetail,
        isLatestPending,
        isLatestError,
        refetch: () => {
          results.forEach((result) => {
            void result.refetch()
          })
        },
      }
    },
  })

  const latestCompletedSessionDetail =
    completedSessionDetailsQuery.latestCompletedSessionDetail
  const latestDisplaySession =
    voiceAssessmentSessions.find(
      (session) => session.status === 'PendingAnalysis',
    ) ?? latestCompletedSession

  const isPending = completedSessionDetailsQuery.isLatestPending
  const isError = completedSessionDetailsQuery.isLatestError

  const refetch = () => {
    void refetchSessions()
    completedSessionDetailsQuery.refetch()
  }

  return (
    <View className="mb-2 mt-4 gap-3">
      {latestCompletedSession?.id == null ? null : isPending ? (
        <View
          className="items-center justify-center py-10"
          style={EXERCISE_GOAL_CARD_STYLE}
        >
          <Spinner />
        </View>
      ) : isError || latestCompletedSessionDetail == null ? (
        <SectionErrorState
          title={t('statistics:voice_assessment_error_title')}
          message={t('statistics:voice_assessment_error_message')}
          onRetry={refetch}
          testID="voice-assessment-statistics-error"
        />
      ) : (
        <>
          <VoiceAssessmentSingingScoreCard
            sessions={completedSessionDetailsQuery.data}
          />
          <VoiceAssessmentRangeCard
            sessions={completedSessionDetailsQuery.data}
          />
        </>
      )}
      <LatestVoiceAssessmentSessionContent
        session={latestDisplaySession}
        refetch={refetch}
      />
    </View>
  )
}

function LatestVoiceAssessmentSessionContent({
  session: latestSession,
  refetch: refetchSessions,
}: {
  session?: SessionSummary | null
  refetch?: () => void
}) {
  const locale = useContentfulLocale()

  const { t } = useTranslation(['statistics'])

  const latestSessionQuery = useQuery({
    ...backendSessionQueryOptions(latestSession?.id ?? 0),
    enabled: latestSession?.id != null,
  })

  const latestSessionDefinitionQuery = useQuery({
    ...sessionDefinitionQueryOptions(latestSession?.definition.id ?? 0, locale),
    enabled: latestSession?.definition.id != null,
  })
  const isPending =
    latestSessionQuery.isPending || latestSessionDefinitionQuery.isPending

  const isError =
    latestSessionQuery.isError || latestSessionDefinitionQuery.isError

  const hasData =
    latestSessionQuery.data != null && latestSessionDefinitionQuery.data != null

  const refetch = () => {
    void refetchSessions?.()
    void latestSessionQuery.refetch()
    void latestSessionDefinitionQuery.refetch()
  }

  const { startVoiceAssessmentSession } = useStartVoiceAssessmentSession()

  if (!latestSession) {
    return (
      <VoiceAssessmentLatestAssessmentEmptyCard
        onStartAssessment={startVoiceAssessmentSession}
      />
    )
  }

  if (isPending) {
    return (
      <View
        className="items-center justify-center py-10"
        style={EXERCISE_GOAL_CARD_STYLE}
      >
        <Spinner />
      </View>
    )
  }

  if (isError || !hasData) {
    return (
      <SectionErrorState
        title={t('statistics:voice_assessment_error_title')}
        message={t('statistics:voice_assessment_error_message')}
        onRetry={refetch}
        testID="voice-assessment-statistics-error"
      />
    )
  }

  if (latestSessionQuery.data.status === 'PendingAnalysis') {
    return (
      <>
        <VoiceAssessmentSectionHeader />
        <VoiceAssessmentLatestAnalyzingCard
          key={latestSessionQuery.data.id}
          dateLabel={toDateLabel(latestSessionQuery.data.created)}
        />
      </>
    )
  }

  return (
    <View className="gap-2 rounded-lg bg-background-secondary px-4 py-3">
      <VoiceAssessmentSectionHeader />
      <VoiceAssessmentExpandedDetailContentBoundary
        sessionId={latestSessionQuery.data.id}
        measurementDefinitions={
          latestSessionDefinitionQuery.data.measurementDefinitions
        }
        showTitles={false}
      />
    </View>
  )
}
